import React from 'react'
import {
    IconBolt,
    IconGraph,
    IconPieChart,
    IconToggle,
    IconRewindPlay,
    IconMessage,
    IconFlask,
    IconLlmAnalytics,
    IconWarning,
    IconAsterisk,
    IconSparkles,
    IconPlug,
} from '@posthog/icons'
import { StickerPath } from 'components/Stickers/Stickers'
import { getTool } from '../../data/tools'

export const posthog_ai = {
    ...getTool('posthog_ai'),
    parentIcon: 'aiMax',
    Icon: IconSparkles,
    role: 'Helpful chatbot',
    type: 'posthog_ai',
    color: 'blue',
    colorSecondary: 'lilac',
    slider: {
        marks: [500, 3000, 10000, 50000, 100000],
        min: 500,
        max: 100000,
    },
    volume: 500,
    customPricingContent: (
        <div data-scheme="secondary" className="prose prose-sm text-lg mt-8 mb-12 leading-normal text-primary">
            <h3 className="text-xl font-bold text-primary mb-4">How credits work</h3>
            <p>
                AI credits are based on the underlying token costs, which reflect the effort required to complete your
                request.
            </p>
            <ul>
                <li>
                    <strong className="text-primary">Simple queries</strong> like "What were my daily active users in
                    October?" use very few tokens, and therefore very few credits.
                </li>
                <li>
                    <strong className="text-primary">More complex tasks</strong> like analyzing hundreds of session
                    recordings or rewriting a SQL query multiple times use more tokens and consume more credits. While
                    exact usage varies, credit consumption usually scales with complexity – more advanced tasks cost
                    more but can deliver deeper insights and time savings. You’ll always see real-time cost information
                    while using AI features.
                </li>
            </ul>
            <p>
                PostHog automatically selects the most efficient model for each AI feature. We apply a simple,
                consistent 20% markup over the underlying LLM provider’s cost: So 1 PostHog AI credit equals $0.008333
                of raw inference, and 100 credits cost $1.
            </p>
        </div>
    ),
    seo: {
        title: 'PostHog AI – Your copilot for PostHog data and insights',
        description:
            'The interface humans and agents use to understand your product – the brain of self-driving. Ask in plain English; PostHog AI queries your data and acts on it.',
    },
    overview: {
        title: 'Ask questions about how people use your product',
        description:
            'PostHog AI builds insights, automates manual tasks, and routes more complex work to specialized agents. It is the interface humans and agents use to understand your product – the brain of self-driving.',
        layout: 'ai',
        textColor: 'text-white',
    },
    screenshots: {
        // overview: {
        //     src: 'https://res.cloudinary.com/dmukukwp6/image/upload/v1/posthog.com/src/components/Product/ProductAnalytics/images/screenshot-product-analytics.png',
        //     alt: 'Product analytics screenshot',
        //     classes: '',
        // },
        sidebarInitial: {
            src: 'https://res.cloudinary.com/dmukukwp6/image/upload/max_sidebar_initial_light_1fbdd896ec.png',
            srcDark: 'https://res.cloudinary.com/dmukukwp6/image/upload/max_sidebar_initial_dark_f80f0d13ac.png',
            alt: 'PostHog AI chat',
            // imgClasses: 'max-h-full'
        },
        sidebarInitialCropped: {
            src: 'https://res.cloudinary.com/dmukukwp6/image/upload/max_sidebar_initial_light_cropped_d4ac0441a1.png',
            srcDark:
                'https://res.cloudinary.com/dmukukwp6/image/upload/max_sidebar_initial_dark_cropped_240b38f95e.png',
            alt: 'PostHog AI chat',
            imgClasses: 'max-w-[444px]',
        },
        home: {
            src: 'https://res.cloudinary.com/dmukukwp6/image/upload/q_auto,f_auto/posthog_ai_light_7b9d11232e.png',
            srcDark: 'https://res.cloudinary.com/dmukukwp6/image/upload/q_auto,f_auto/posthog_ai_dark_f98f323528.png',
            alt: 'PostHog AI screenshot',
            classes: 'justify-center items-center',
            imgClasses: 'rounded shadow-2xl border border-primary',
        },
    },
    hog: {
        src: 'https://res.cloudinary.com/dmukukwp6/image/upload/ai_max_e80de99727.png',
        alt: 'Hello, PostHog AI!',
        classes: 'max-w-[413px]',
    },
    features: [
        {
            label: 'Analytics',
        },
        {
            title: 'Product Analytics',
            headline: 'Product Analytics',
            team: 'product-analytics',
            layout: 'ai',
            icon: <IconGraph className="size-5" />,
            color: 'blue',
            description:
                'Less digging - more dialogue. PostHog AI lives in your product data. Create insights and dashboards, and generate complex HogQL queries with natural language.',
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/product_analytics_5bf99d9574.png',
                    alt: 'Product analytics',
                    className: 'h-80 w-full object-contain @2xl:h-64',
                    // stylize: true,
                    // shadow: true,
                },
            ],
            skills: [
                {
                    name: 'Build insights from plain English',
                    description: 'Describe the data you want to see visualized as an insight or dashboard',
                    sticker: <StickerPath className="size-6" />,
                    percent: 90,
                },
                {
                    name: 'Write and explain HogQL',
                    description: 'Create complex SQL queries and get an explanation of the logic behind them',
                    sticker: <StickerPath className="size-6" />,
                    percent: 70,
                },
                {
                    name: 'Deep dive into your product data',
                    description: 'Try research mode for deeper analysis across multiple context sources',
                    sticker: <StickerPath className="size-6" />,
                    percent: 90,
                },
                {
                    name: 'Navigate the UI',
                    description: 'Edit filters, set breakdowns, build retention curves (and more) with simple prompts',
                    sticker: <StickerPath className="size-6" />,
                    percent: 70,
                },
                // {
                //     name: 'Anomaly detection',
                //     description: 'Detect outliers with AI and configure alerts to catch them',
                //     sticker: <StickerPath className="size-6" />,
                //     percent: 0,
                // },
            ],
        },
        {
            title: 'Web Analytics',
            headline: 'Web Analytics',
            team: 'web-analytics',
            layout: 'ai',
            icon: <IconPieChart className="size-5" />,
            color: 'green',
            description:
                "Privacy-friendly web analytics that doesn't require selling your soul (or your users' data). PostHog AI surfaces why traffic tanked, and what's actually converting.",
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/web_analytics_a2ab8ea550.png',
                    alt: 'Web analytics',
                    className: 'h-80 w-full object-contain @2xl:h-64',
                    // stylize: true,
                    // shadow: true,
                },
            ],
            skills: [
                {
                    name: 'Prompt to apply filters',
                    description:
                        'Filter web traffic by page path, geography, device type, or referrer using plain language',
                    sticker: <StickerPath className="size-12" />,
                    percent: 70,
                },
                {
                    name: 'Decode acquisition channels',
                    description: 'Understand which channels are actually driving quality visitors, not just volume',
                    sticker: <StickerPath className="size-12" />,
                    percent: 50,
                },
                {
                    name: 'Correlate web traffic with product data',
                    description: 'Connect what happens on the marketing site to what users do inside your product',
                    sticker: <StickerPath className="size-12" />,
                    percent: 70,
                },
                {
                    name: 'Analyze web vitals',
                    description:
                        'Investigate traffic spikes, dips, and other anomalies to optimize website performance',
                    sticker: <StickerPath className="size-12" />,
                    percent: 70,
                },
            ],
        },
        {
            title: 'AI Observability',
            headline: 'AI Observability',
            team: 'ai-observability',
            layout: 'ai',
            icon: <IconLlmAnalytics className="size-5" />,
            color: 'purple',
            description:
                "Why was today's token spend more than your salary? PostHog AI and AI Observability help keep an eye on your AI.",
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/llm_cde5a95040.png',
                    alt: 'AI Observability',
                    className: 'h-80 w-full object-contain @2xl:h-64',
                    // stylize: true,
                    // shadow: true,
                },
            ],
            skills: [
                {
                    name: 'Summarize LLM traces',
                    description: 'Understand complex, multi-step interactions without reading full logs',
                    sticker: <StickerPath className="size-6" />,
                    percent: 90,
                },
                {
                    name: 'Monitor usage and adoption',
                    description: 'Connect AI feature usage with other events and user actions in your product',
                    sticker: <StickerPath className="size-6" />,
                    percent: 90,
                },
                {
                    name: 'Forecast costs and token consumption',
                    description: "Ask PostHog AI to project next month's spend based on last month's usage",
                    sticker: <StickerPath className="size-6" />,
                    percent: 50,
                },
                {
                    name: 'Discover patterns in LLM usage',
                    description: 'Get AI-generated summaries for clusters of similar LLM traces',
                    sticker: <StickerPath className="size-6" />,
                    percent: 50,
                },
            ],
        },
        {
            label: 'Product engineering',
        },
        {
            title: 'Session Replay',
            headline: 'Session Replay',
            team: 'replay',
            layout: 'ai',
            icon: <IconRewindPlay className="size-5" />,
            color: 'yellow',
            description:
                "Don't scrub through hours of recordings – let a robot suffer instead. Ask PostHog AI for the bloopers, sizzle reel, or directors cut of user behavior.",
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/session_reply_6846989ead.png',
                    alt: 'Session replay',
                    className: 'h-80 w-full object-contain @2xl:h-64',
                    // stylize: true,
                    // shadow: true,
                },
            ],
            skills: [
                {
                    name: 'Search for specific recordings',
                    description: 'Filter recordings using natural language (e.g., "users who abandoned checkout")',
                    sticker: <StickerPath className="size-6" />,
                    percent: 90,
                },
                {
                    name: 'Summarize user behavior',
                    description:
                        'Get a summary of one or more recordings, including patterns, errors, and key findings',
                    sticker: <StickerPath className="size-6" />,
                    percent: 70,
                },
                {
                    name: 'Cluster similar sessions',
                    description: 'Group similar sessions and surface representative examples from thousands',
                    sticker: <StickerPath className="size-6" />,
                    percent: 70,
                },
                {
                    name: 'Rank sessions by signal',
                    description: 'Ask PostHog AI for session recordings worth watching based on specific criteria',
                    sticker: <StickerPath className="size-6" />,
                    percent: 50,
                },
            ],
        },
        {
            title: 'Feature Flags',
            headline: 'Feature Flags',
            team: 'feature-flags',
            layout: 'ai',
            icon: <IconToggle className="size-5" />,
            color: 'green',
            description:
                'PostHog AI sets up, monitors, manages, and rolls out your feature flags – making releases safe by default.',
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/feature_flags_ce422c1e73.png',
                    alt: 'Feature Flags',
                    className: 'h-80 w-full object-contain @2xl:h-64',
                },
            ],
            skills: [
                {
                    name: 'Configure flags with natural language',
                    description: 'Describe the flag you want to create and PostHog AI will handle the rest',
                    sticker: <StickerPath className="size-6" />,
                    percent: 70,
                },
                {
                    name: 'Detect stale flags',
                    description:
                        'Scan for feature flags not in use, and automatically remove them from your app and codebase',
                    sticker: <StickerPath className="size-6" />,
                    percent: 50,
                },
                {
                    name: 'Monitor flags in production',
                    description: 'Replace manual checks with prompts to get a clear picture of flag performance',
                    sticker: <StickerPath className="size-6" />,
                    percent: 50,
                },
                {
                    name: 'Understand rollout rules',
                    description: 'Get a simple explanation of how a flag is configured and who it is targeting',
                    sticker: <StickerPath className="size-6" />,
                    percent: 50,
                },
            ],
        },
        {
            title: 'Experiments',
            headline: 'Experiments',
            team: 'experiments',
            layout: 'ai',
            icon: <IconFlask className="size-5" />,
            color: 'purple',
            description:
                'Ship ideas like a mad scientist. PostHog AI handles setup, flags, and metrics – you decide what deploys (and which experiments never leave the lab).',
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/experiments_f90ed26268.png',
                    alt: 'Experiments',
                    className: 'h-80 w-full object-contain @2xl:h-64',
                    // stylize: true,
                    // shadow: true,
                },
            ],
            skills: [
                {
                    name: 'Set up A/B tests with prompts',
                    description:
                        'Create and configure experiments, variants, and target metrics using natural language',
                    sticker: <StickerPath className="size-6" />,
                    percent: 70,
                },
                {
                    name: 'Interpret experiment results',
                    description:
                        'Identify winning variants through AI summaries, and get recommendations for next steps',
                    sticker: <StickerPath className="size-6" />,
                    percent: 50,
                },
                {
                    name: 'Troubleshoot setup issues',
                    description: 'Ensure statistically valid and reliable results by scanning for common issues',
                    sticker: <StickerPath className="size-6" />,
                    percent: 30,
                },
                {
                    name: 'Connect wins to downstream metrics',
                    description: 'Analyze the impact of winning experiments across product metrics and user behavior',
                    sticker: <StickerPath className="size-6" />,
                    percent: 50,
                },
            ],
        },
        {
            title: 'Error Tracking',
            headline: 'Error Tracking',
            team: 'error-tracking',
            layout: 'ai',
            icon: <IconWarning className="size-5" />,
            color: 'orange',
            description:
                'Find out what broke before your users tweet about it. PostHog AI connects exceptions to user sessions, revenue impact, and business context – not just stack traces.',
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/error_tracking_53fc0bc180.png',
                    alt: 'Error Tracking',
                    className: 'h-80 w-full object-contain @2xl:h-64',
                    // stylize: true,
                    // shadow: true,
                },
            ],
            skills: [
                {
                    name: 'Search for specific errors',
                    description: 'Ask PostHog AI to find specific errors with natural language prompts',
                    sticker: <StickerPath className="size-6" />,
                    percent: 70,
                },
                {
                    name: 'Score errors by impact',
                    description: 'Ask PostHog AI to surface exceptions worth investigating based on downstream impact',
                    sticker: <StickerPath className="size-6" />,
                    percent: 50,
                },
                {
                    name: 'Summarize a stack trace',
                    description: 'Understand the likely cause of an exception without reading every line',
                    sticker: <StickerPath className="size-6" />,
                    percent: 50,
                },
                {
                    name: 'Add context to exceptions',
                    description: 'Use natural language to get the full context of affected users and related sessions',
                    sticker: <StickerPath className="size-6" />,
                    percent: 30,
                },
            ],
        },
        {
            label: 'Communication',
        },
        {
            title: 'Surveys',
            headline: 'Surveys',
            team: 'surveys',
            layout: 'ai',
            icon: <IconMessage className="size-5" />,
            color: 'salmon',
            description:
                "Describe what you want to know, get a working survey in seconds. It's never been easier to get roasted by users (and advised on next steps by PostHog AI).",
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/surveys_aeb8302376.png',
                    alt: 'Surveys',
                    className: 'h-80 w-full object-contain @2xl:h-64',
                    // stylize: true,
                    // shadow: true,
                },
            ],
            skills: [
                {
                    name: 'Create surveys with prompts',
                    description: 'Generate questions, set up targeting, and launch surveys with natural language',
                    sticker: <StickerPath className="size-6" />,
                    percent: 90,
                },
                {
                    name: 'Summarize survey responses',
                    description: 'Ask PostHog AI to surface themes and patterns from aggregated user feedback',
                    sticker: <StickerPath className="size-6" />,
                    percent: 70,
                },
                {
                    name: 'Filter responses by sentiment',
                    description: 'Sort through responses by meaning, not just keywords',
                    sticker: <StickerPath className="size-6" />,
                    percent: 30,
                },
                {
                    name: 'Craft questions with AI',
                    description: 'Use the right question types for your research goal, not just generic options',
                    sticker: <StickerPath className="size-6" />,
                    percent: 50,
                },
            ],
        },
        {
            title: 'Workflows',
            headline: 'Workflows',
            team: 'workflows',
            layout: 'ai',
            icon: <IconBolt className="size-5" />,
            color: 'blue',
            description:
                'Why drag and drop when you can just ask? PostHog AI builds the workflow, sets the triggers, and ships it faster than you can say <em>“if this, then that.”</em>',
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/workflows_81cfc6f841.png',
                    alt: 'Workflows',
                    className: 'h-80 w-full object-contain @2xl:h-64',
                    // stylize: true,
                    // shadow: true,
                },
            ],
            skills: [
                {
                    name: 'Generate email templates',
                    description: 'Ask PostHog AI to build dynamic templates in your content library',
                    sticker: <StickerPath className="size-6" />,
                    percent: 50,
                },
                {
                    name: 'Set up multi-step workflows',
                    description: 'Describe the conditions, triggers, and actions, get a working workflow in seconds',
                    sticker: <StickerPath className="size-6" />,
                    percent: 30,
                },
                {
                    name: 'Connect workflows to business metrics',
                    description: 'Get recommendations for which workflows to build and when to run them',
                    sticker: <StickerPath className="size-6" />,
                    percent: 30,
                },
            ],
        },
        {
            label: 'PostHog data stack',
        },
        {
            title: 'Data Stack',
            headline: 'Data Stack',
            team: 'data-stack',
            layout: 'ai',
            icon: <IconAsterisk className="size-5" />,
            color: 'purple',
            description:
                'Dump all your Stripe, Hubspot, and whatever-else data into one place. PostHog AI generates HogQL queries to join tables you forgot existed.',
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/data_warehouse_db4a9498c8.png',
                    alt: 'Data Warehouse',
                    className: 'h-80 w-full object-contain @2xl:h-64',
                    // stylize: true,
                    // shadow: true,
                },
            ],
            skills: [
                {
                    name: 'Fix SQL errors',
                    description:
                        'Use PostHog AI to correct syntax and logic errors to ensure queries return the data you need',
                    sticker: <StickerPath className="size-6" />,
                    percent: 70,
                },
                {
                    name: 'Join tables with AI',
                    description:
                        'Use natural language to join PostHog data with external sources and visualize the results',
                    sticker: <StickerPath className="size-6" />,
                    percent: 50,
                },
                {
                    name: 'Translate complex queries',
                    description: 'Get an explanation of what a query does and what it returns in plain English',
                    sticker: <StickerPath className="size-6" />,
                    percent: 70,
                },
            ],
        },
        {
            title: 'Data Pipelines',
            headline: 'Data Pipelines',
            team: 'data-modeling',
            layout: 'ai',
            icon: <IconPlug className="size-5" />,
            color: 'blue',
            description:
                'PostHog AI is your data plumber. It finds the leaks, clears the clogs, and keeps insights flowing without flooding your warehouse.',
            images: [
                {
                    src: 'https://res.cloudinary.com/dmukukwp6/image/upload/ingestion_7b729b78f7.png',
                    alt: 'Data Pipelines',
                    className: 'h-80 w-full object-contain @2xl:h-64',
                    // stylize: true,
                    // shadow: true,
                },
            ],
            skills: [
                {
                    name: 'Set up data pipelines with prompts',
                    description: 'Configure a Slack alert, webhook, or warehouse export from a simple description',
                    sticker: <StickerPath className="size-6" />,
                    percent: 70,
                },
                {
                    name: 'Transform your event data',
                    description: 'Ask PostHog AI to write custom logic to modify and enrich event data',
                    sticker: <StickerPath className="size-6" />,
                    percent: 70,
                },
                {
                    name: 'Audit your data pipeline',
                    description:
                        "Summarize which destinations are active, what they're receiving, and any anomalies worth investigating",
                    sticker: <StickerPath className="size-6" />,
                    percent: 70,
                },
            ],
        },
    ],
    videos: {
        investigating_web_traffic: {
            title: 'Investigating web traffic',
            author: 'Edwin Lim',
            wistia: 'tgws1dixc0',
            customThumb:
                'https://res.cloudinary.com/dmukukwp6/image/upload/thumb_investigating_web_traffic_86c3caa67d.png',
            chapters: [
                {
                    title: 'Add a graph series for unique users',
                    time: 60,
                    copyable: true,
                },
                {
                    title: 'Create a dashboard of insights with traffic breakdowns by referral, country, user agent, and operating system',
                    time: 110,
                    copyable: true,
                },
                {
                    title: 'Find session recordings with filters based on the IP address over the last 60 days',
                    time: 246,
                    copyable: true,
                },
                {
                    title: 'Summarize these session recordings in a report for me and provide key findings',
                    time: 285,
                    copyable: true,
                },
            ],
        },
        maximizing_data_insights: {
            title: 'Maximizing data insights',
            author: 'Eric Duong',
            wistia: 'syysfftbzk',
            customThumb:
                'https://res.cloudinary.com/dmukukwp6/image/upload/thumb_maximizing_data_insights_60ecdcb060.png',
            chapters: [
                {
                    title: 'Last 10 Stripe charges',
                    time: 18,
                    copyable: true,
                },
                {
                    title: 'All charges last 14 days with email',
                    time: 41,
                    copyable: true,
                },
                {
                    title: 'Make this query a CTE and bring in refunds as another CTE. Match the select values and union them for the result.',
                    time: 81,
                    copyable: true,
                },
                {
                    title: "In Stripe data, what's the net amount charged and refunded in the last 7 days?",
                    time: 140,
                    copyable: true,
                },
            ],
        },
        answering_business_questions: {
            title: 'Answering business questions',
            author: 'Georgiy Tarasov',
            wistia: 'hjr4vq1py4',
            customThumb:
                'https://res.cloudinary.com/dmukukwp6/image/upload/thumb_answering_business_questions_aed4278f7a.png',
            chapters: [
                {
                    title: "What's the cache hit rate for AI generations?",
                    time: 30,
                    copyable: true,
                },
                {
                    title: 'Break down the chart by model',
                    time: 45,
                    copyable: true,
                },
                {
                    title: 'Include customer feedback for AI generations from the last 7 days',
                    time: 67,
                    copyable: true,
                },
                {
                    title: 'Summarize session recordings for the given filters',
                    time: 104,
                    copyable: true,
                },
            ],
        },
        integrating_external_data: {
            title: 'Integrating external data',
            author: 'Natlaia Amorim',
            wistia: '8yephrnt6h',
            customThumb:
                'https://res.cloudinary.com/dmukukwp6/image/upload/thumb_integrating_external_data_ab4b91a189.png',
            chapters: [
                {
                    title: 'How can I import a google sheet into PostHog so I can use it as a data source',
                    time: 55,
                    copyable: true,
                },
                {
                    title: 'Now my table has been synced and is available as a data source. Help me create an insight where I can show our keyword rankings evolution over time (the Y axis should be the count, and X axis should be the month string and the ranking string.',
                    time: 143,
                    copyable: true,
                },
            ],
        },
    },
    answers: [
        {
            q: "What's my churn rate?",
        },
        {
            q: 'Show me user retention by country',
        },
        {
            q: "What's our most popular feature?",
        },
        {
            q: "What's my ARR?",
        },
        {
            q: 'Where do my users drop off?',
        },
        {
            q: 'What are my most popular pages?',
        },
        {
            q: 'What is distribution of paid vs. organic traffic?',
        },
        {
            q: 'Write a SQL query for me',
        },
        {
            q: 'How many pageviews did we get today?',
        },
        {
            q: 'Show me a signup funnel',
        },
    ],
    postHogOnPostHog: {
        title: 'How PostHog uses PostHog AI',
        benefits: [
            {
                title: 'Build insights and dashboards',
                description: 'using natural language',
            },
            {
                title: 'Create SQL queries',
                description: 'to use for detailed analysis',
            },
            {
                title: 'Summarize session replays',
                description: "so we don't have to watch them all",
            },
            {
                title: 'Get product advice',
                description: 'because we trained the AI on our blog too',
            },
            {
                title: 'Fix bugs in our implementation',
                description: 'way faster than reading all the docs',
            },
        ],
    },
    comparison: {
        comparison_companies: {
            Segment: true,
            mParticle: true,
            RudderStack: true,
            Fivetran: true,
        },
        comparison_rows: [
            {
                feature: 'Number of integrations',
                companies: {
                    Segment: '300+',
                    mParticle: '300+',
                    RudderStack: '200+',
                    Fivetran: '500+',
                    PostHog: '60+',
                },
            },
            {
                feature: 'Real-time streaming',
                companies: {
                    Segment: true,
                    mParticle: true,
                    RudderStack: true,
                    Fivetran: false,
                    PostHog: true,
                },
            },
            {
                feature: 'Batch exports',
                companies: {
                    Segment: true,
                    mParticle: true,
                    RudderStack: true,
                    Fivetran: true,
                    PostHog: true,
                },
            },
            {
                feature: 'Data warehouse sources',
                companies: {
                    Segment: false,
                    mParticle: false,
                    RudderStack: true,
                    Fivetran: true,
                    PostHog: true,
                },
            },
            {
                feature: 'Built-in analytics',
                companies: {
                    Segment: false,
                    mParticle: false,
                    RudderStack: false,
                    Fivetran: false,
                    PostHog: true,
                },
            },
            {
                feature: 'Starting price',
                companies: {
                    Segment: '$120/mo',
                    mParticle: 'Contact',
                    RudderStack: '$500/mo',
                    Fivetran: '$120/mo',
                    PostHog: 'Free sources',
                },
            },
        ],
    },
    pairsWith: [
        {
            slug: 'product-analytics',
            description:
                'Get your source data into PostHog, then analyze it alongside your product data to unlock new insights and discover new user behaviors.',
        },
        {
            slug: 'data-warehouse',
            description:
                'Build a data warehouse in PostHog and then pull in data from all your platforms to one place where it can be easily interrogated.',
        },
    ],
    presenterNotes: {
        // overview:
        //     "<strong>Presenter notes:</strong> PostHog AI is an AI agent that lives inside PostHog and actually understands your product data. It's not a chatbot slapped onto a dashboard. You can ask it to build insights, write HogQL queries, summarize session recordings, create surveys, set up feature flags—basically handle the grunt work that normally takes 20 minutes of clicking around. It routes complex tasks to specialized AI agents and uses context from your actual data. The big difference: it's trained on PostHog's data model and your specific setup, so it actually knows what it's doing.",
        'posthog-on-posthog':
            "We use PostHog AI constantly. Like, genuinely use it—not in a 'marketing uses our own product once for a screenshot' way. Our engineers use it to write complex SQL queries against our data warehouse without having to remember every table schema. Product managers use it to build dashboards in seconds instead of bothering engineering. Support uses it to summarize session replays when debugging user issues. We trained it on our blog and docs too, so it can answer questions about how to use PostHog without you having to search through documentation. It's become the fastest way to go from question to answer.",
        features:
            "Each PostHog product has its own set of AI capabilities, and they're pretty specific to what you'd actually want to do in that context.<br /><br />In <strong>Product Analytics</strong>, you can generate insights and dashboards from plain English. Write 'show me DAU over the last month broken down by country' and it builds the chart. It also writes HogQL queries and explains what they do, which is useful when you're learning the syntax or dealing with complex joins.<br /><br />For <strong>Session Replay</strong>, you can search recordings using natural language ('users who abandoned checkout'), and it'll find the relevant sessions. You can also ask it to cluster similar sessions and pull out representative examples from thousands of recordings, which saves hours of manual review.<br /><br /><strong>Feature Flags</strong> setup becomes conversational. Describe what you want to roll out and it configures the flag. It can also detect stale flags in your codebase—super useful for cleanup.<br /><br /><strong>Experiments</strong> get easier too. Create A/B tests by describing what you're testing, and get AI-generated analysis of results with recommendations for next steps.<br /><br />For <strong>Surveys</strong>, describe what you want to learn and it generates questions, sets targeting rules, and later synthesizes responses to surface themes without you reading every single answer.<br /><br />In <strong>Error Tracking</strong>, you can search exceptions with natural language and get impact scoring based on affected users and business context—not just stack traces.<br /><br /><strong>AI Observability</strong> is where it gets meta: use LLM-as-a-judge to evaluate your own LLM traces at scale, summarize complex interactions, and analyze token spend without drowning in logs.<br /><br />The <strong>Data Warehouse</strong> integration means it can help fix SQL errors, generate queries that join your external data sources, and navigate schemas you don't have memorized.<br /><br />And with <strong>Workflows</strong>, you can build multi-step automations and generate email templates using natural language instead of dragging boxes around a UI builder.<br /><br />It's not about replacing what you do—it's about handling the repetitive parts so you can focus on the actual decision-making.",
        answers:
            "These are real questions our users ask. The useful thing about PostHog AI is it's not just searching your data—it's building the analysis for you. Want to know your churn rate? It'll create the cohort definition, run the calculation, and show you the visualization. Need to understand where users drop off? It builds the funnel and then lets you jump to session recordings of those exact users. The SQL query requests are probably the most common—people know what data they want but don't want to spend 30 minutes remembering the exact syntax and table names. It's basically your coworker who's really good at PostHog and has time to help.",
        pricing:
            "We charge for AI based on the actual token usage from the underlying LLM providers, with a 20% markup. One PostHog AI credit equals $0.008333 of raw inference cost, so 500 credits is $5. Simple queries like 'what were my daily active users in October?' use very few credits, maybe 50-100 credits. More complex tasks like analyzing hundreds of session recordings or rewriting SQL queries multiple times will consume more, but you see the cost in real-time so there are no surprises.<br /><br />Everyone starts with 500 free credits per month. After that, you pay for what you use. The more credits you need, the cheaper they get (volume pricing). We automatically route to the most efficient model for each task, so you're not stuck paying GPT-4 prices when a smaller model works fine.<br /><br />The thing is, even complex queries are usually cheaper than the time you'd spend building them manually. A 500-credit task that saves you 20 minutes is still a good deal. And unlike seat-based pricing, your whole team can use it without multiplying costs.",
        'comparison-summary':
            "Most analytics platforms either (a) don't have AI, (b) slapped ChatGPT onto their dashboard and called it 'AI-powered,' or (c) have AI that only does one thing like anomaly detection. PostHog AI is different because it's deeply integrated into every product and actually understands your data model. It can write valid HogQL, knows your event names, understands your feature flags—it's not just a general-purpose LLM trying to help. Because it sits on that context and can act on it, it's the brain that makes your product self-driving, not just another chatbot.<br /><br />The closest comparison is probably Amplitude's AI, but that only works inside Amplitude. We're open source, so you can see exactly how it works and even self-host if you want. Our pricing is also transparent and usage-based instead of requiring an enterprise contract.",
        docs: "The docs for PostHog AI explain how it works under the hood—what models we use, how we handle context, what each AI agent specializes in. We're upfront about limitations too: it's not perfect, it can make mistakes, and you should verify important queries before acting on them. That said, it's getting better fast. We're constantly training it on more examples and improving the prompts.<br /><br />If you run into issues or have ideas for how AI could be more useful in specific workflows, tell us. The team actively monitors feedback because this is still early days and we're trying to build something that's actually useful, not just a checkbox feature.",
        'pairs-with':
            "PostHog AI works across the entire platform, so it pairs with everything. But it's especially powerful when you combine products. For example: ask AI to find session replays of users who hit a specific error, then have it create a feature flag to roll out the fix to just those affected users, then build an experiment to test if it actually solved the problem. All of that without leaving PostHog or manually matching user IDs across tools.<br /><br />It also works with our data warehouse, so you can query external data from Stripe, HubSpot, etc. alongside your product data. Ask 'show me users who churned in the last 30 days and their LTV from Stripe' and it writes the join for you.",
        'getting-started':
            "PostHog AI is available in the sidebar of most PostHog pages—just click the icon and start asking questions. If you're not sure what to ask, try something simple like 'show me my top pages from last week' or 'create a funnel for signup to activation.' You'll immediately see how it interprets your request and builds the analysis.<br /><br />As you use it more, you'll learn what works well. Be specific when you can ('users in the US who signed up after Jan 1st'), and don't be afraid to iterate ('now add a breakdown by device type'). It's designed to have a conversation, not require perfect prompts on the first try.<br /><br />The AI credits you use are visible in real-time, so you'll quickly get a sense of what costs what. Most teams find that the time savings far outweigh the credit cost—especially for tasks like session replay analysis or complex SQL queries that would otherwise take significant manual effort.",
        demo: "These are all real questions you can ask PostHog AI right now. The examples show the range of what's possible—from high-level product questions ('What changed this week?') to specific technical tasks ('Write SQL for this query'). Notice they're not perfectly formatted prompts. You can be conversational. If you're signed into PostHog, you can click any of these and it'll actually start working on it for you. The demo cards highlight different capabilities: connecting data points across the platform, summarizing recordings, building dashboards from plain English, analyzing LLM usage (meta!), web traffic analysis, SQL debugging, joining external data, and answering 'how do I use PostHog' questions. The point is: it's not a toy. It's solving real problems that would otherwise eat up engineering time.",
        videos: "These are screen recordings of PostHog engineers actually using PostHog AI for real work—not staged demos. Watch Edwin investigate web traffic by asking AI to add graph series, create a dashboard with traffic breakdowns, find specific session recordings, and then summarize them into a report. Eric shows how to query Stripe data in the data warehouse, starting with simple requests and building up to complex CTEs and unions. Georgiy demonstrates business analysis: checking cache hit rates, breaking down by model, pulling in customer feedback, and summarizing session recordings. The videos show you can be conversational and iterate on your requests. You don't need to know the perfect query syntax upfront. The AI helps you refine as you go. These are the kinds of tasks that normally require SQL knowledge, data engineering skills, or significant manual work. PostHog AI handles them in minutes.",
        you: "This slide shows how PostHog AI helps different roles, but here's the real insight: it's most valuable when it removes bottlenecks between teams. Founders get instant answers without waiting for engineering. Product Engineers can analyze data without context-switching to another tool. Product Managers can run experiments and dig into data without needing a data analyst. Growth teams can build dashboards without SQL knowledge. Data Analysts can offload repetitive 'quick questions' and focus on harder problems. The common thread: everyone becomes more self-sufficient. You're not creating ticket queues or Slack threads asking someone else to pull data. You ask PostHog AI, verify the output makes sense, and keep moving. It's not about replacing these roles—it's about making each role more effective at their actual job instead of being blocked waiting for someone else.",
        roadmap:
            "We're constantly shipping new AI capabilities and making existing ones deeper. Some examples: anomaly detection that explains why metrics changed, semantic grouping of errors by root cause instead of just stack traces, configuration assistants that set up features using natural language. The goal isn't to automate everything — it's to handle the repetitive pattern-matching work so humans can focus on the decisions that actually require judgment. We're also training the AI on more domain knowledge so it gets better at understanding product analytics concepts, not just executing commands. Check the roadmap regularly because we're shipping new stuff constantly, and you can vote on what you want us to prioritize next.",
    },
}
